import { useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Widget from '@jimmyboys/liita-ui/dist/components/Widget'
import RuleEntry from './RuleEntry/RuleEntry'

import styles from './RulesEditor.module.sass'
import Rule from 'models/Rule'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useSectionTitle from 'hooks/useSectionTitle'

// Alocation rules UserIds; Tags; Coordinator
// output: { "type": "UserIds", "output": ["t1174"] }

const RulesEditor = ({
	id,
	options,
	ruleType,
	taskType,
	onSubmit,
	scrollableClassName,
	ignoreRulerOrder,
	outputDefault,
}) => {
	const { i18n } = useGlobal()
	const title = useSectionTitle(i18n, 'taskType')
	const { setValue } = useFormContext()
	const { fields, append, remove } = useFieldArray({
		name: 'rules',
		keyName: 'key',
	})

	const maxOrder = (fields[fields.length - 1]?.order ?? 0) + 1

	const orderOptions = useMemo(
		() =>
			new Array(maxOrder)
				.fill(null)
				.map((_, index) => ({ value: index, text: `${index + 1}` })),
		[maxOrder]
	)

	const appendNewRule = (event) => {
		const rule = new Rule(
			undefined,
			i18n`Rule ${fields.length + 1}`,
			ruleType,
			taskType,
			fields.length,
			true,
			null,
			null
		).withLogicalConditions([
			[
				{
					option: null,
					optionGroup: null,
					operator: null,
					value: null,
				},
			],
		])
		if (outputDefault) {
			rule.withOutput(outputDefault, taskType)
		}

		append(rule)
		const scrollableContainer = event.target.closest(
			`.${scrollableClassName}`
		)
		setTimeout(
			(scrollableElement) => {
				if (scrollableElement) {
					scrollableElement.scrollTop =
						scrollableElement.scrollHeight + 100
				}
			},
			0,
			scrollableContainer
		)
	}

	let finalOptions = { ...options }

	if (ruleType === 'PriorityRule' && taskType === 'FinalSituationPrevision') {
		finalOptions = {
			...finalOptions,
			fields: [
				...finalOptions.fields,
				{ value: 'previsionType', text: i18n`previsionType` },
			],
		}
	}

	return (
		<>
			<form
				id={id}
				className={styles['form-container']}
				onSubmit={onSubmit}
				onKeyPress={(event) => {
					if (document.activeElement.tagName !== 'TEXTAREA') {
						event.key === 'Enter' && event.preventDefault()
					}
				}}
			>
				{fields.map(({ key, ...data }, index) => (
					<RuleEntry
						key={key}
						path={`rules.${index}`}
						index={index}
						ruleType={ruleType}
						taskType={taskType}
						data={{ ...data, taskTypeTitle: title }}
						options={{
							...finalOptions,
							order: ignoreRulerOrder ? undefined : orderOptions,
						}}
						onResetEntry={() => {
							if (data.action && data.actionEntity) {
								setValue(`rules.${index}.logicalConditions`, [
									{
										logicalCondition: [
											{
												option: null,
												optionGroup: null,
												operator: null,
												value: null,
											},
										],
									},
								])
							}
						}}
						onDelete={() => {
							remove(index)
						}}
					/>
				))}
			</form>
			<Widget
				className={styles['footer']}
				paddings={['xsmall', 'xsmall']}
				margins={['normal', '', 'xsmall', '']}
			>
				<HStack align='center' gap='xsmall'>
					<div>
						<h5>{i18n`Actions`}</h5>
					</div>
					<ActionButton primary onClick={appendNewRule}>
						{i18n`Add a rule`}
					</ActionButton>
				</HStack>
			</Widget>
		</>
	)
}

export default RulesEditor
